import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { 
  Globe, 
  Mail, 
  MessageSquare, 
  CalendarDays, 
  BarChart3, 
  Bot, 
  CreditCard, 
  Star,
  Users,
  FileText,
  Zap,
  Phone,
  Target,
  Layout,
  GitBranch,
} from 'lucide-react';
import storilyIcon from '@/assets/storily-icon.png';
import storilyIconDark from '@/assets/storily-icon-dark.png';

const AnimatedPricingMockup: React.FC = () => {
  const { theme } = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [phase, setPhase] = useState(0); // 0=scattered, 1=converging, 2=unified, 3=complete
  const sectionRef = useRef<HTMLDivElement>(null);

  const isDark = theme === 'dark';
  
  const gridLineColor = isDark ? '#444444' : '#d0d0d0';
  const iconColor = isDark ? '#888888' : '#666666';
  
  const iconBoxStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(to right, ${gridLineColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)`,
    backgroundSize: '5px 5px',
    WebkitMaskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
    maskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
  };

  // Inner ring - 8 icons evenly spaced at 45° intervals
  const innerTools = [
    { Icon: Globe, angle: 0, distance: 70, delay: 0, label: 'Websites' },
    { Icon: Mail, angle: 45, distance: 70, delay: 50, label: 'Email' },
    { Icon: MessageSquare, angle: 90, distance: 70, delay: 100, label: 'SMS' },
    { Icon: CalendarDays, angle: 135, distance: 70, delay: 150, label: 'Bookings' },
    { Icon: BarChart3, angle: 180, distance: 70, delay: 200, label: 'Analytics' },
    { Icon: Bot, angle: 225, distance: 70, delay: 250, label: 'AI Agents' },
    { Icon: CreditCard, angle: 270, distance: 70, delay: 300, label: 'Payments' },
    { Icon: Zap, angle: 315, distance: 70, delay: 350, label: 'Automation' },
  ];

  // Outer ring - 8 icons evenly spaced, offset by 22.5° from inner ring
  const outerTools = [
    { Icon: Users, angle: 22.5, distance: 115, delay: 25, label: 'CRM' },
    { Icon: FileText, angle: 67.5, distance: 115, delay: 75, label: 'Forms' },
    { Icon: Zap, angle: 112.5, distance: 115, delay: 125, label: 'Automation' },
    { Icon: Phone, angle: 157.5, distance: 115, delay: 175, label: 'Calls' },
    { Icon: Target, angle: 202.5, distance: 115, delay: 225, label: 'Funnels' },
    { Icon: Layout, angle: 247.5, distance: 115, delay: 275, label: 'Pages' },
    { Icon: GitBranch, angle: 292.5, distance: 115, delay: 325, label: 'Workflows' },
    { Icon: Star, angle: 337.5, distance: 115, delay: 375, label: 'Reviews' },
  ];

  // Combine all tools
  const allTools = [...innerTools, ...outerTools];

  // Calculate x,y position from angle and distance
  const getPosition = (angle: number, distance: number) => {
    const radians = (angle * Math.PI) / 180;
    return {
      x: Math.cos(radians) * distance,
      y: Math.sin(radians) * distance,
    };
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    // Phase progression - reduced pause between converge and logo grow
    const timers = [
      setTimeout(() => setPhase(1), 1500),  // Start converging
      setTimeout(() => setPhase(2), 2200),  // Unified (reduced from 3000ms)
      setTimeout(() => setPhase(3), 3000),  // Complete with tagline (reduced from 3800ms)
      setTimeout(() => setPhase(4), 5700),  // Start fade out (reduced from 6500ms)
    ];

    // Smooth loop - fade out then reset
    const resetTimer = setTimeout(() => {
      setPhase(0);
    }, 6700);

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(resetTimer);
    };
  }, [isVisible, phase === 0 ? Date.now() : null]);

  return (
    <div 
      ref={sectionRef} 
      className="w-full max-w-md mx-auto"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
      }}
    >
      <div 
        className="relative h-72 overflow-hidden"
        style={{ 
          backgroundColor: 'transparent',
        }}
      >

        {/* Central Hub - Icon only, no background square */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div 
            className="relative flex flex-col items-center justify-center"
            style={{
              opacity: phase >= 2 && phase < 4 ? 1 : (phase === 4 ? 0 : 0.3),
              transform: phase >= 2 && phase < 4 ? 'scale(1)' : 'scale(0.6)',
              transition: 'all 0.7s ease-out',
            }}
          >
            <img 
              src={isDark ? storilyIconDark : storilyIcon}
              alt="Storily"
              width={64}
              height={64}
              className="transition-all duration-500 object-contain"
              style={{ 
                width: phase >= 2 && phase < 4 ? '64px' : '32px',
                height: phase >= 2 && phase < 4 ? '64px' : '32px',
                filter: phase >= 2 && phase < 4 
                  ? (isDark 
                      ? 'drop-shadow(0 0 20px rgba(255,255,255,0.3))' 
                      : 'drop-shadow(0 0 20px rgba(0,0,0,0.15))')
                  : 'none',
              }} 
            />
          </div>

          {/* Pulse rings when unified */}
          {phase >= 2 && phase < 4 && (
            <>
              <div 
                className="absolute rounded-full"
                style={{
                  width: '120px',
                  height: '120px',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
                  animation: 'pricing-ripple 2s ease-out infinite',
                }}
              />
              <div 
                className="absolute rounded-full"
                style={{
                  width: '120px',
                  height: '120px',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)'}`,
                  animation: 'pricing-ripple 2s ease-out infinite 0.5s',
                }}
              />
            </>
          )}
        </div>

        {/* Orbiting/Scattered Icons */}
        {allTools.map((tool, index) => {
          const { x, y } = getPosition(tool.angle, tool.distance);
          const Icon = tool.Icon;
          
          return (
            <div
              key={index}
              className="absolute left-1/2 top-1/2 -ml-5 -mt-5"
              style={{
                transform: phase >= 1 
                  ? 'translate(0, 0) scale(0)' 
                  : `translate(${x}px, ${y}px) scale(1)`,
                opacity: phase >= 1 ? 0 : 1,
                transition: `all 0.8s cubic-bezier(0.4, 0, 0.2, 1) ${tool.delay}ms`,
              }}
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{
                  ...iconBoxStyle,
                  animation: phase === 0 ? `pricing-float-bob ${2 + (index % 3) * 0.5}s ease-in-out infinite ${index * 0.2}s` : 'none',
                }}
              >
                <Icon 
                  className="w-5 h-5"
                  style={{ color: iconColor }}
                  strokeWidth={1.5}
                />
              </div>
            </div>
          );
        })}


        {/* Tagline at bottom */}
        <div 
          className="absolute bottom-4 left-0 right-0 text-center"
          style={{
            opacity: phase >= 3 && phase < 4 ? 1 : 0,
            transform: phase >= 3 && phase < 4 ? 'translateY(0)' : 'translateY(10px)',
            transition: 'all 0.5s ease-out',
          }}
        >
          <p 
            className="text-xs font-medium tracking-wide"
            style={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)' }}
          >
            One Platform. One Price.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AnimatedPricingMockup;